import { SuccessToast } from '../adminComponents/Toasts/Toasts';
import { reportError } from '../helpers/reportError';
import { useCallback, useState } from 'react';

/**
 * Runs one write at a time, and reports it.
 *
 * Every membership and metadata change in the conversation modals is the same
 * four lines - flag busy, await, toast, report the failure - and repeating them
 * per action is how one of them ends up without the guard and fires twice on a
 * double click. The support tickets page claims, releases and resolves the
 * same way, which is what moved this out of the chat page and up here.
 *
 * `busy` is what the buttons disable on. `after` runs on success only, before
 * the toast, for the refresh both modals do.
 *
 * Resolves to whether the write actually went through, so a caller can close
 * itself on success and stay open - with whatever the user had picked still
 * there - when it failed.
 */
export const useBusyAction = (after?: () => void) => {
  const [busy, setBusy] = useState(false);

  const run = useCallback(
    async (action: () => Promise<unknown>, message?: string) => {
      if (busy) return false;
      try {
        setBusy(true);
        await action();
        if (message) SuccessToast(message);
        after?.();
        return true;
      } catch (error) {
        reportError(error);
        return false;
      } finally {
        setBusy(false);
      }
    },
    [busy, after]
  );

  return { busy, run };
};
